import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { SPRING_POP } from '../../../shared/motion.ts'
import { fetchCrashRound } from '../api/auth'
import type { HistoryItem } from '../ws/types'

type Props = {
  history: HistoryItem[]
  token: string | null
}

function colorFor(m: number): string {
  if (m < 2) return 'hist-red'
  if (m <= 10) return 'hist-blue'
  return 'hist-green'
}

export function RoundHistory({ history, token }: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState<HistoryItem | null>(null)
  const [extra, setExtra] = useState<HistoryItem | null>(null)
  const [busy, setBusy] = useState(false)

  const show = async (item: HistoryItem) => {
    setOpen(item)
    setExtra(null)
    if (item.serverSeed || !item.roundId || !token) return
    setBusy(true)
    try {
      const row = await fetchCrashRound(token, item.roundId)
      setExtra({
        crashPoint: row.crash_point ?? item.crashPoint,
        roundId: row.id,
        serverSeed: row.server_seed,
        serverSeedHash: row.server_seed_hash,
        clientSeed: row.client_seed,
        algorithm: row.algorithm,
      })
    } catch {
      /* keep chip-only view */
    } finally {
      setBusy(false)
    }
  }

  if (!history.length) {
    return <div className="history empty">{t('noHistory')}</div>
  }
  const detail = extra ?? open
  return (
    <>
      <div className="history">
        {history.map((m, i) => (
          <motion.button
            key={`${m.roundId ?? m.crashPoint}-${i}`}
            type="button"
            layout
            className={`hist-chip ${colorFor(m.crashPoint)}`}
            initial={{ opacity: 0, scale: 0.6, y: -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={SPRING_POP}
            onClick={() => void show(m)}
          >
            {m.crashPoint.toFixed(2)}x
          </motion.button>
        ))}
      </div>
      {open ? (
        <div className="fair-card">
          <p className="fair-title">{t('fair.title', { mult: open.crashPoint.toFixed(2) })}</p>
          {busy ? <p className="hint">{t('fair.loading')}</p> : null}
          {detail?.algorithm ? (
            <p className="hint mono">
              {t('fair.algo')}: {detail.algorithm}
            </p>
          ) : null}
          {detail?.serverSeedHash ? (
            <p className="hint mono wrap">{t('fair.hash')}: {detail.serverSeedHash}</p>
          ) : null}
          {detail?.serverSeed ? (
            <p className="hint mono wrap">{t('fair.seed')}: {detail.serverSeed}</p>
          ) : (
            <p className="hint">{t('fair.hidden')}</p>
          )}
          <button type="button" className="btn-lobby" onClick={() => setOpen(null)}>
            {t('fair.close')}
          </button>
        </div>
      ) : null}
    </>
  )
}
